import { type FC, PureComponent } from 'react';
import { useLocation } from 'react-router';

interface Props {
  location: {
    hash: string;
  };
}

/**
 * Scrolls to the element with an ID which matches the current location.hash
 * (if any). Useful for restoring scroll position after reload on a page with
 * anchors that scroll to specific locations on the page.
 *
 * It also "fixes" in-page links in Safari which don't seem to work when used in
 * conjunction with React Router.
 */
class ScrollToElement extends PureComponent<Props> {
  observer?: ResizeObserver;
  timeout?: ReturnType<typeof setTimeout>;

  componentDidMount() {
    this.handleHashChange();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.location.hash !== prevProps.location.hash) {
      this.handleHashChange();
    }
  }

  componentWillUnmount(): void {
    clearTimeout(this.timeout);
    this.observer?.disconnect();
  }

  handleHashChange = () => {
    const hash = this.props.location.hash.substring(1);

    if (hash.length > 0) {
      const target = document.querySelector(`#${hash}`);

      if (target != null) {
        if ('scrollRestoration' in history) {
          history.scrollRestoration = 'manual';
        }

        clearTimeout(this.timeout);
        this.observer?.disconnect();

        // Use a ResizeObserver to wait for the element to finish rendering
        // before attempting to scroll to it.
        this.observer = new ResizeObserver(([entry]) => {
          if (entry == null) {
            return;
          }

          if (entry.contentRect.height > 0) {
            target.scrollIntoView({
              block: 'start',
              inline: 'nearest',
            });

            this.observer?.disconnect();
          }
        });

        this.observer.observe(target);

        // If the element hasn't finished rendering after 250ms, give up and
        // disconnect the observer.
        this.timeout = setTimeout(() => {
          this.observer?.disconnect();
        }, 250);
      }
    }
  };

  render() {
    return null;
  }
}

const ScrollToElementWithLocation: FC = () => {
  const location = useLocation();

  return <ScrollToElement location={location} />;
};

export default ScrollToElementWithLocation;
